import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Lock, LogOut, Mail, MapPin, Package, Trash2, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "ml_admin_token";

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!password.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(STORAGE_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const msg =
        err?.response?.status === 401
          ? "Incorrect password."
          : "Login failed. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-screen"
      className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6"
    >
      <div className="w-full max-w-md border border-[#1A1A1A]/10 bg-white p-10">
        <p className="overline mb-6">Studio · Private</p>
        <h1 className="font-serif-editorial text-4xl md:text-5xl leading-none text-[#1A1A1A] font-light">
          Maison<span className="italic text-[#6B5C4D]"> Locale</span>
        </h1>
        <p className="mt-4 text-[#5C564D] leading-relaxed">
          Enter the studio password to review inquiries.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" noValidate>
          <label className="flex flex-col">
            <span className="overline mb-3">Password</span>
            <div className="relative">
              <Lock
                className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A89680]"
                strokeWidth={1.5}
              />
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                className="w-full bg-transparent border-b border-[#1A1A1A]/25 focus:border-[#1A1A1A] focus:border-b-2 outline-none py-3 pl-7 text-base transition-all"
              />
            </div>
          </label>

          {error && (
            <p
              data-testid="admin-login-error"
              className="text-sm text-red-700 border-l-2 border-red-700 pl-3"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center h-14 bg-[#1A1A1A] text-[#FAF9F6] text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-[#A89680] disabled:opacity-60 transition-colors duration-500"
          >
            {submitting ? "Entering…" : "Enter Studio"}
          </button>
        </form>

        <a
          href="/"
          className="mt-8 inline-block text-xs uppercase tracking-[0.22em] text-[#A89680] hover-underline"
        >
          ← Back to site
        </a>
      </div>
    </div>
  );
}

function LeadsList({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { "X-Admin-Password": token },
      });
      setLeads(data);
      if (data.length && !selected) setSelected(data[0]);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Failed to load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return leads;
    return leads.filter((l) =>
      [l.name, l.email, l.property_location, l.property_type, l.package_interest, l.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(t))
    );
  }, [leads, q]);

  useEffect(() => {
    if (selected && !filtered.find((l) => l.id === selected.id)) {
      setSelected(filtered[0] || null);
    }
  }, [filtered, selected]);

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, {
        headers: { "X-Admin-Password": token },
      });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Inquiry removed.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-dashboard">
      <header className="border-b border-black/10 bg-white/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-baseline gap-4">
            <a
              href="/"
              className="font-serif-editorial text-2xl leading-none text-[#1A1A1A]"
              data-testid="admin-brand-link"
            >
              Maison<span className="text-[#A89680]">·</span>Locale
            </a>
            <span className="overline">Studio · Inquiries</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchLeads}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 h-10 px-4 border border-[#1A1A1A]/15 text-[11px] tracking-[0.22em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 h-10 px-4 bg-[#1A1A1A] text-white text-[11px] tracking-[0.22em] uppercase hover:bg-[#A89680] transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* List */}
          <aside className="lg:col-span-5">
            <div className="mb-6 flex items-baseline justify-between">
              <h1 className="font-serif-editorial text-4xl md:text-5xl leading-none text-[#1A1A1A] font-light">
                Inquiries
                <span className="font-sans text-sm text-[#A89680] ml-3 align-middle uppercase tracking-[0.22em]">
                  {leads.length}
                </span>
              </h1>
            </div>

            <input
              data-testid="admin-search"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, message…"
              className="w-full bg-transparent border-b border-[#1A1A1A]/20 focus:border-[#1A1A1A] focus:border-b-2 outline-none py-3 mb-6 text-base"
            />

            {loading ? (
              <p className="text-[#5C564D]" data-testid="admin-loading">Loading…</p>
            ) : filtered.length === 0 ? (
              <div
                data-testid="admin-empty"
                className="border border-dashed border-[#1A1A1A]/20 p-10 text-center"
              >
                <p className="overline mb-3">Nothing yet</p>
                <p className="text-[#5C564D]">
                  No inquiries {q ? "match this search" : "have arrived"}. This
                  is where they&apos;ll appear.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-[#1A1A1A]/10 border-t border-[#1A1A1A]/10">
                {filtered.map((l) => (
                  <li key={l.id}>
                    <button
                      onClick={() => setSelected(l)}
                      data-testid={`admin-lead-row-${l.id}`}
                      className={`w-full text-left py-5 flex items-baseline justify-between gap-4 hover:bg-[#F2EEE6] px-3 -mx-3 transition-colors ${
                        selected?.id === l.id ? "bg-[#F2EEE6]" : ""
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-serif-editorial text-xl md:text-2xl text-[#1A1A1A] truncate">
                          {l.name}
                        </p>
                        <p className="text-sm text-[#5C564D] truncate">
                          {l.email} {l.package_interest ? `· ${l.package_interest}` : ""}
                        </p>
                      </div>
                      <span className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-[#A89680]">
                        {fmt(l.created_at).split(",")[0]}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Detail */}
          <section className="lg:col-span-7" data-testid="admin-lead-detail">
            {selected ? (
              <article className="border border-[#1A1A1A]/10 bg-white p-8 md:p-12">
                <div className="flex items-start justify-between gap-4 mb-8">
                  <div>
                    <p className="overline mb-3">
                      Received {fmt(selected.created_at)}
                    </p>
                    <h2 className="font-serif-editorial text-4xl md:text-5xl leading-none text-[#1A1A1A]">
                      {selected.name}
                    </h2>
                  </div>
                  <button
                    onClick={() => remove(selected.id)}
                    data-testid="admin-lead-delete"
                    className="inline-flex items-center gap-2 h-10 px-4 border border-[#1A1A1A]/15 text-[11px] tracking-[0.22em] uppercase hover:bg-red-700 hover:text-white hover:border-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
                  </button>
                </div>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-10">
                  <Field label="Email" icon={Mail}>
                    <a
                      href={`mailto:${selected.email}`}
                      className="text-[#1A1A1A] hover-underline"
                      data-testid="admin-lead-email"
                    >
                      {selected.email}
                    </a>
                  </Field>
                  <Field label="Property" icon={MapPin}>
                    {selected.property_type || "—"}
                    {selected.property_location ? ` · ${selected.property_location}` : ""}
                  </Field>
                  <Field label="Package" icon={Package}>
                    {selected.package_interest || "—"}
                  </Field>
                </dl>

                <div className="border-t border-[#1A1A1A]/10 pt-6">
                  <p className="overline mb-4">Their message</p>
                  <p className="font-serif-editorial text-xl italic text-[#1A1A1A] leading-relaxed whitespace-pre-wrap border-l-2 border-[#A89680] pl-5">
                    {selected.message}
                  </p>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-3">
                  <a
                    href={`mailto:${selected.email}?subject=${encodeURIComponent(
                      "Re: your inquiry with Maison Locale"
                    )}`}
                    data-testid="admin-lead-reply"
                    className="inline-flex items-center gap-2 h-12 px-6 bg-[#1A1A1A] text-white text-[11px] tracking-[0.22em] uppercase hover:bg-[#A89680] transition-colors"
                  >
                    Reply by email
                  </a>
                </div>
              </article>
            ) : (
              <div className="border border-dashed border-[#1A1A1A]/20 p-16 text-center text-[#5C564D]">
                Select an inquiry to view details.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <p className="overline mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
        {label}
      </p>
      <p className="text-[#3E3227] text-base leading-relaxed">{children}</p>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    document.title = "Studio · Inquiries — Maison Locale";
  }, []);

  const logout = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore storage errors */
    }
    setToken("");
  };

  return (
    <>
      {token ? (
        <LeadsList token={token} onLogout={logout} />
      ) : (
        <AdminLogin onLogin={setToken} />
      )}
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#1A1A1A",
            color: "#FAF9F6",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: "2px",
            fontFamily: "'Outfit', sans-serif",
            letterSpacing: "0.02em",
          },
        }}
      />
    </>
  );
}
